import React from 'react';
import {
  Chip,
  Stack,
  Button,
  Link as MuiLink,
  Typography,
  Tooltip,
  CircularProgress,
  Table,
  TableContainer,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
} from '@mui/material';
import {
  Edit as EditIcon,
  Block as BlockIcon,
  ExpandMore,
  OpenInNew,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountStats } from '../../../hooks/api/useAccountStats';
import { getAccountStatus, isAccountExpired } from '../utils';
import dayjs from 'dayjs';
import { SortableHeaderButton } from '../../../components/TableHeaderButton';
import { AccountStatusBadge } from './AccountStatusBadge';

const arrowStylingDefault = {
  transition: 'transform 150ms ease',
};

const arrowStyling = {
  transform: 'rotate(-180deg)',
  transition: 'transform 150ms ease',
};

type SortableField =
  | '_id'
  | 'name'
  | 'status'
  | 'contract_expiration_date'
  | 'last_login'
  | 'active'
  | 'expired';

interface AccountsTableDesktopProps {
  accounts: AccountStats[];
  orderBy: SortableField;
  order: 'asc' | 'desc';
  onSort: (property: SortableField) => void;
  loggedInAccount: string;
  searchTerm: string;
  expandedExtensions: Set<string>;
  onToggleExtension: (key: string) => void;
  loadingRows: Set<string>;
  onDisableClick: (accountId: string, event: React.MouseEvent) => void;
  onEdit: (account: AccountStats, event: React.MouseEvent) => void;
  onAgreementClick: (accountId: string, event: React.MouseEvent) => void;
  onBrandingClick: (accountId: string, event: React.MouseEvent) => void;
  searchUrl: string;
}

export const AccountsTableDesktop: React.FC<AccountsTableDesktopProps> = ({
  accounts,
  orderBy,
  order,
  onSort,
  loggedInAccount,
  searchTerm,
  expandedExtensions,
  onToggleExtension,
  loadingRows,
  onDisableClick,
  onEdit,
  onAgreementClick,
  onBrandingClick,
  searchUrl,
}) => {
  const { t } = useTranslation();

  const getArrowIcon = (key: SortableField) => {
    const isActive = orderBy === key;
    const rotation = isActive && order === 'asc' ? 180 : 0;
    return (
      <ExpandMore
        fontSize="small"
        style={{
          ...arrowStylingDefault,
          ...(rotation && arrowStyling),
          opacity: isActive ? 1 : 0.5,
        }}
      />
    );
  };

  return (
    <TableContainer
      sx={{
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Table
        sx={{
          width: '100%',
          tableLayout: 'auto',
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>
              <SortableHeaderButton
                variant="secondary"
                onClick={() => onSort('_id')}
                sx={{ justifyContent: 'left' }}
                isSorted={orderBy === '_id'}
                sortDirection={order}
              >
                {t('accounts.accountId')}
                {getArrowIcon('_id')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell>
              <SortableHeaderButton
                variant="secondary"
                onClick={() => onSort('name')}
                sx={{ justifyContent: 'left' }}
                isSorted={orderBy === 'name'}
                sortDirection={order}
              >
                {t('accounts.name')}
                {getArrowIcon('name')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell>
              <SortableHeaderButton
                variant="secondary"
                onClick={() => onSort('status')}
                sx={{ justifyContent: 'left' }}
                isSorted={orderBy === 'status'}
                sortDirection={order}
              >
                {t('accounts.status')}
                {getArrowIcon('status')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell>
              <SortableHeaderButton
                variant="secondary"
                onClick={() => onSort('active')}
                sx={{ justifyContent: 'left' }}
                isSorted={orderBy === 'active'}
                sortDirection={order}
              >
                {t('accounts.active')}
                {getArrowIcon('active')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell>
              <SortableHeaderButton
                variant="secondary"
                onClick={() => onSort('expired')}
                sx={{ justifyContent: 'left' }}
                isSorted={orderBy === 'expired'}
                sortDirection={order}
              >
                {t('accounts.expired')}
                {getArrowIcon('expired')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell>
              <SortableHeaderButton
                variant="secondary"
                onClick={() => onSort('contract_expiration_date')}
                sx={{ justifyContent: 'left' }}
                isSorted={orderBy === 'contract_expiration_date'}
                sortDirection={order}
              >
                {t('accounts.contractExpiration')}
                {getArrowIcon('contract_expiration_date')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell>
              <SortableHeaderButton
                variant="secondary"
                onClick={() => onSort('last_login')}
                sx={{ justifyContent: 'left' }}
                isSorted={orderBy === 'last_login'}
                sortDirection={order}
              >
                {t('accounts.lastLogin')}
                {getArrowIcon('last_login')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell sx={{ justifyContent: 'left' }}>
              {t('accounts.extensions')}
            </TableCell>
            {loggedInAccount === 'admin' && (
              <TableCell>{t('accounts.actions')}</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.map((account) => {
            const isExpired = isAccountExpired(
              account.contract_expiration_date
            );
            const isLoading = loadingRows.has(account._id);
            return (
              <TableRow
                key={account._id}
                sx={{
                  opacity: isLoading ? 0.5 : 1,
                  ...(isExpired && {
                    opacity: 0.6,
                    '& td': {
                      color: 'text.disabled',
                    },
                  }),
                }}
              >
                <TableCell>
                  <Button
                    size="small"
                    variant={
                      account.agreements?.active ? 'link' : 'warningLink'
                    }
                    endIcon={<OpenInNew fontSize="small" />}
                    href={`${searchUrl}/${account._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {account._id}
                  </Button>
                </TableCell>
                <TableCell>
                  {account.name ? (
                    <Tooltip title={t('accounts.goToBranding')}>
                      <MuiLink
                        component="button"
                        type="button"
                        onClick={(e) => onBrandingClick(account._id, e)}
                        sx={{
                          display: 'inline-flex',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          background: 'transparent',
                          border: 0,
                          padding: 0,
                          cursor: 'pointer',
                          font: 'inherit',
                        }}
                      >
                        {account.name}
                      </MuiLink>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <AccountStatusBadge status={getAccountStatus(account)} />
                </TableCell>
                <TableCell>
                  {account.agreements?.active ? (
                    <Chip
                      label={`${account.agreements.active.agreements}/${account.agreements.active.journals}`}
                      size="medium"
                      variant="success"
                      onClick={(e) => onAgreementClick(account._id, e)}
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {account.agreements?.expired ? (
                    <Chip
                      label={`${account.agreements.expired.agreements}/${account.agreements.expired.journals}`}
                      size="medium"
                      variant="warning"
                      onClick={(e) => onAgreementClick(account._id, e)}
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {account.contract_expiration_date
                    ? dayjs(account.contract_expiration_date).format(
                        'YYYY-MM-DD'
                      )
                    : '-'}
                </TableCell>
                <TableCell>
                  {account.last_login
                    ? dayjs(account.last_login).format('YYYY-MM-DD')
                    : '-'}
                </TableCell>
                <TableCell>
                  {account.extensions &&
                  Object.keys(account.extensions).length > 0 ? (
                    <Stack spacing={0.5}>
                      {Object.entries(account.extensions).map(
                        ([key, values]) => {
                          const extensionKey = `${account._id}-${key}`;
                          const isExpanded =
                            expandedExtensions.has(extensionKey);
                          return (
                            <Chip
                              key={key}
                              size="small"
                              variant="default"
                              clickable
                              onClick={() => onToggleExtension(extensionKey)}
                              label={
                                !Array.isArray(values)
                                  ? `${key}: ${values}`
                                  : isExpanded
                                    ? `${key}: ${values.length} (${values.join(', ')})`
                                    : `${key}: ${values.length}`
                              }
                            />
                          );
                        }
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                </TableCell>
                {loggedInAccount === 'admin' && (
                  <TableCell>
                    <Stack direction="row" spacing={2}>
                      <Tooltip title={t('accounts.edit')}>
                        <span>
                          <Button
                            size="small"
                            variant="secondary"
                            onClick={(e) => onEdit(account, e)}
                            aria-label={t('accounts.edit') as string}
                            disabled={isLoading}
                            sx={{
                              minWidth: 'unset',
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('accounts.disable')}>
                        <span>
                          <Button
                            size="small"
                            variant="warningLink"
                            onClick={(e) => onDisableClick(account._id, e)}
                            aria-label={t('accounts.disable') as string}
                            disabled={isLoading}
                            sx={{
                              minWidth: 'unset',
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                            }}
                          >
                            {isLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <BlockIcon fontSize="small" />
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {accounts.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={loggedInAccount === 'admin' ? 9 : 8}
                align="center"
                sx={{ py: 4 }}
              >
                <Typography variant="body1">
                  {searchTerm
                    ? t('accounts.noAccountsFoundWithSearch', {
                        searchTerm,
                      })
                    : t('accounts.noAccountsFound')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
